import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Avatar } from '../components/Avatar'
import { EventResponseInline } from '../components/EventResponseInline'
import { Sheet } from '../components/Sheet'
import { useApp } from '../store/AppContext'
import { localISODate } from '../lib/date'

export function GroupChatThread() {
  const { groupId } = useParams<{ groupId: string }>()
  const { currentUser, allUsers, groups, messages, sendGroupMessage, markGroupThreadRead } = useApp()
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const [showSchedule, setShowSchedule] = useState(false)

  const group = groups.find((g) => g.id === groupId)

  const participants = useMemo(
    () => (group ? group.participantIds.map((id) => allUsers.find((u) => u.id === id)).filter(Boolean) : []),
    [group, allUsers],
  ) as ReturnType<typeof allUsers.find>[]

  const thread = useMemo(() => {
    if (!groupId) return []
    return messages.filter((m) => m.threadId === groupId).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  }, [messages, groupId])

  useEffect(() => {
    if (groupId) markGroupThreadRead(groupId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, thread.length])

  if (!group) {
    return (
      <div className="p-4">
        <p className="text-sm text-slate-500">Group not found.</p>
      </div>
    )
  }

  const submit = () => {
    if (!text.trim() || !groupId) return
    sendGroupMessage(groupId, text)
    setText('')
  }

  return (
    <div className="flex h-screen flex-col">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+12px)] backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/chats')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"
          >
            ‹
          </button>
          <div className="flex -space-x-2">
            {participants.slice(0, 4).map((p) => p && <Avatar key={p.id} user={p} size={28} />)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900">{group.name}</p>
            <p className="truncate text-xs text-slate-500">{participants.map((p) => p?.name.split(' ')[0]).join(', ')}</p>
          </div>
          <button
            onClick={() => setShowSchedule(true)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600"
            aria-label="Schedule a meeting"
          >
            📅
          </button>
        </div>
      </header>

      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4 pb-28">
        {thread.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">
            Say hello to the group.
          </div>
        )}
        {thread.map((m) => {
          const mine = m.senderId === currentUser.id
          const sender = allUsers.find((u) => u.id === m.senderId)
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] ${mine ? '' : 'flex items-end gap-2'}`}>
                {!mine && sender && <Avatar user={sender} size={24} />}
                <div>
                  {!mine && sender && <p className="mb-0.5 ml-0.5 text-[11px] font-semibold text-slate-500">{sender.name}</p>}
                  <div
                    className={`rounded-2xl px-3.5 py-2.5 text-sm shadow-card ${
                      mine ? 'rounded-br-md bg-brand-600 text-white' : 'rounded-bl-md bg-white text-slate-800'
                    }`}
                  >
                    <p>{m.text}</p>
                    {m.eventId && !mine && <EventResponseInline eventId={m.eventId} />}
                    <p className={`mt-1 text-[10px] ${mine ? 'text-brand-100' : 'text-slate-400'}`}>
                      {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="Message the group..."
            className="input flex-1"
          />
          <button
            onClick={submit}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white"
          >
            ➤
          </button>
        </div>
      </div>

      <Sheet open={showSchedule} onClose={() => setShowSchedule(false)} title="Schedule a meeting">
        <ScheduleMeetingForm groupId={group.id} onClose={() => setShowSchedule(false)} />
      </Sheet>
    </div>
  )
}

function ScheduleMeetingForm({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const { currentUser, groups, addEvent, sendGroupMessage } = useApp()
  const group = groups.find((g) => g.id === groupId)!

  const [title, setTitle] = useState('Team Meeting')
  const [date, setDate] = useState(localISODate())
  const [startTime, setStartTime] = useState('10:00')
  const [endTime, setEndTime] = useState('10:30')
  const [notes, setNotes] = useState('')

  const submit = () => {
    if (!title.trim()) return
    const assignees = group.participantIds.filter((id) => id !== currentUser.id).map((userId) => ({ userId, status: 'pending' as const }))
    const newEventId = `ev-${Date.now()}`

    addEvent({
      id: newEventId,
      title: title.trim(),
      type: 'meeting',
      date,
      startTime,
      endTime,
      assignees,
      createdByUserId: currentUser.id,
      notes: notes.trim() || undefined,
      sourceGroupId: groupId,
    })

    const niceDate = new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
    sendGroupMessage(groupId, `📅 ${currentUser.name} scheduled "${title.trim()}" for ${niceDate}, ${startTime}–${endTime}.`, newEventId)

    onClose()
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
        This will be sent to everyone in {group.name} ({group.participantIds.length - 1} other{group.participantIds.length - 1 === 1 ? '' : 's'}) to accept or decline.
      </p>
      <label className="block text-xs font-semibold text-slate-600">
        <span className="mb-1 block">Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
      </label>
      <label className="block text-xs font-semibold text-slate-600">
        <span className="mb-1 block">Date</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" />
      </label>
      <div className="flex gap-3">
        <label className="block flex-1 text-xs font-semibold text-slate-600">
          <span className="mb-1 block">Start</span>
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input" />
        </label>
        <label className="block flex-1 text-xs font-semibold text-slate-600">
          <span className="mb-1 block">End</span>
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input" />
        </label>
      </div>
      <label className="block text-xs font-semibold text-slate-600">
        <span className="mb-1 block">Notes (optional)</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input" />
      </label>

      <div className="mt-1 flex gap-2">
        <button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600">
          Cancel
        </button>
        <button onClick={submit} className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">
          Schedule
        </button>
      </div>
    </div>
  )
}

